import type { InspectionRecord } from '../api/types'
import { formatDate, formatLongDate } from '../domain/format'

/**
 * The inspection history as a vertical timeline, newest first. It is the one place in the report
 * where chronology carries meaning.
 *
 * An inspection with defects gets an amber marker and lists them. The RDW retires defect codes, so
 * a defect on an old inspection can arrive without a description; the code is shown on its own
 * rather than dropping the entry, because "something was found here" is itself information.
 */
export function InspectionTimeline({ history }: { history: InspectionRecord[] }) {
  if (history.length === 0) {
    return null
  }

  return (
    <ol className="timeline">
      {history.map((record, index) => {
        const defects = record.defects ?? []
        const reported = formatLongDate(record.reportedOn)

        return (
          <li
            // Two inspections can share a date, so the index is part of the identity. The list is
            // never reordered or filtered, so this is stable.
            key={`${record.reportedOn ?? 'onbekend'}-${index}`}
            className={`timeline__item${defects.length > 0 ? ' timeline__item--faulted' : ''}`}
          >
            {reported && <p className="timeline__date">{reported}</p>}
            <h3 className="timeline__title">{record.inspectionType ?? 'Keuring'}</h3>

            <p className="timeline__meta">
              {record.reportType && <>{record.reportType}</>}
              {record.validUntil && (
                <>
                  {record.reportType ? ', ' : ''}
                  geldig tot {formatDate(record.validUntil)}
                </>
              )}
              {defects.length === 0 && (record.reportType || record.validUntil ? ', ' : '')}
              {defects.length === 0 && 'geen bevindingen'}
            </p>

            {defects.length > 0 && (
              <ul className="defects">
                {defects.map((defect) => (
                  <li className="defect" key={defect.code}>
                    <span className="defect__code">{defect.code}</span>
                    <span>
                      {defect.description ?? 'Omschrijving niet meer gepubliceerd door de RDW'}
                      {defect.count > 1 && <span className="defect__count"> ({defect.count}×)</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        )
      })}
    </ol>
  )
}
